import apiService from './api';
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  User,
  UserProfile,
} from '@/types/user';

// Backend response format
interface BackendAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    is_external: boolean;
    is_admin?: boolean;
    organization_id?: string;
    preferences?: Record<string, any>;
  };
}

// Transform backend response to frontend format
function transformAuthResponse(backendResponse: BackendAuthResponse): AuthResponse {
  return {
    user: {
      id: backendResponse.user.id,
      email: backendResponse.user.email,
      name: `${backendResponse.user.first_name} ${backendResponse.user.last_name}`,
      role: 'admin',
      isActive: backendResponse.user.is_active,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      is_admin: backendResponse.user.is_admin,
      organization_id: backendResponse.user.organization_id,
      preferences: backendResponse.user.preferences || {},
    },
    tokens: {
      accessToken: backendResponse.access_token,
      refreshToken: backendResponse.refresh_token,
    },
  };
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiService.post<BackendAuthResponse>('/auth/login', credentials);
    return transformAuthResponse(response);
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiService.post<BackendAuthResponse>('/auth/register', data);
    return transformAuthResponse(response);
  }

  async logout(): Promise<void> {
    return apiService.post<void>('/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
    return apiService.post<{ accessToken: string }>('/auth/refresh', {
      refreshToken,
    });
  }

  async getCurrentUser(): Promise<User> {
    return apiService.get<User>('/users/me');
  }

  async getUserProfile(): Promise<UserProfile> {
    return apiService.get<UserProfile>('/users/me');
  }

  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    return apiService.patch<UserProfile>('/users/me', data);
  }

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    return apiService.post<void>('/auth/change-password', data);
  }

  async requestPasswordReset(email: string): Promise<void> {
    return apiService.post<void>('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    return apiService.post<void>('/auth/reset-password', {
      token,
      newPassword,
    });
  }

  async verifyEmail(token: string): Promise<void> {
    return apiService.post<void>('/auth/verify-email', { token });
  }
}

export const authService = new AuthService();
export default authService;
